import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useId } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { useViewingAs } from '@/auth/useViewingAs';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard, StatCardRow } from '@/components/ui/StatCard';
import { formatCount } from '@/lib/numbers';
import { testIdPart } from '@/lib/testIds';

import { percentChange } from './change';
import { CurrentActivityCard } from './CurrentActivityCard';
import { CARDS, LATER_RELEASE, METRIC_TABLES } from './dashboardData';
import { DateRangeSelect } from './DateRangeSelect';
import { parseRange, toSearchParams, todayIso, type ResolvedRange } from './dateRange';
import { EngagementTrendCard } from './EngagementTrendCard';
import { MetricTable } from './MetricTable';
import { useDashboard } from './useDashboard';

/** Req §19, and the wireframe: every number here is an interaction, not an outcome. */
export const FOOTNOTE =
  'Interaction metrics reflect activity measurable inside ActivistARC. A button click does not confirm that a call, email, volunteer action, or donation was completed.';

/** Two columns side by side at desktop width, one above the other below it. */
const twoUp = (columns: string) =>
  ({
    display: 'grid',
    gap: 2,
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: columns },
    mb: 3,
  }) as const;

/**
 * The organization's front page: how people engage with it inside
 * ActivistARC over a chosen range, from the organization's own analytics.
 *
 * The range lives in the address, so a reload and a shared link keep it. The
 * header, the range control and the footnote are there in every state; what
 * the numbers fill in holds its size while they load, so nothing moves when
 * they arrive.
 */
export function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const today = todayIso();
  const range = parseRange(searchParams, today);
  const { user } = useAuth();
  const { viewing } = useViewingAs();
  // The organization the numbers are for: the one staff are viewing, else the
  // signed-in person's open one.
  const query = useDashboard(range, viewing?.id ?? user?.organizationId ?? null);
  const pdfNoteId = useId();

  const data = query.data;
  const loading = query.isPending;
  const failed = query.isError;
  // A new range for the same organization: the last numbers stay on screen
  // until the new ones arrive (P12).
  const refreshing = query.isPlaceholderData;
  const retry = () => void query.refetch();

  function changeRange(next: ResolvedRange) {
    // Replaced rather than pushed: a custom range is typed a day at a time,
    // and the back button should not step through every half-typed pair.
    setSearchParams(toSearchParams(next), { replace: true });
  }

  return (
    <Box data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        description="Organization activity and performance at a glance"
        action={
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <DateRangeSelect value={range} onChange={changeRange} today={today} />
            {/* A disabled button fires no pointer events, so the tooltip sits
                on a wrapper. The note is also tied to the button, so a
                screen reader that finds it hears why it does nothing. */}
            <Tooltip title={LATER_RELEASE} describeChild>
              <span>
                <Button
                  variant="secondary"
                  disabled
                  aria-describedby={pdfNoteId}
                  data-testid="dashboard-download-pdf-button"
                >
                  Download PDF
                </Button>
              </span>
            </Tooltip>
            <Box component="span" id={pdfNoteId} hidden>
              {LATER_RELEASE}
            </Box>
          </Box>
        }
      />

      {/* What the range means, in every state and after the control in
          reading order: the counts are hourly and there is no timestamp to
          show, and a preset includes today's partial UTC day (P13). */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'flex-start', sm: 'flex-end' },
          mt: -1,
          mb: 2,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="dashboard-freshness-hint"
        >
          Counts update hourly
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="dashboard-range-utc-hint"
        >
          Includes today; days are UTC
        </Typography>
        {/* Plain text, not a live region: the busy mark on the numbers is
            the announcement, and a live region would talk on every change. */}
        {refreshing && (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="dashboard-refreshing-hint"
          >
            Refreshing…
          </Typography>
        )}
      </Box>

      {/* The numbers' region: busy while a new range loads over the last
          numbers. The header, the hints and the footnote stay outside it. */}
      <Box aria-busy={refreshing || undefined}>
        {failed ? (
          <Box sx={{ mb: 3 }}>
            {/* The alert keeps the ID it was published under before the shared
                state existed; the retry's ID is what the prefix gives anyway. */}
            <ErrorState
              title="The dashboard could not be loaded."
              onRetry={retry}
              testId="dashboard"
              alertTestId="dashboard-error-alert"
            />
          </Box>
        ) : (
          <>
            <StatCardRow label="Summary" testId="dashboard-summary-group">
              {CARDS.map((metric) => {
                const numbers = data?.cards[metric.id];
                return (
                  <StatCard
                    key={metric.id}
                    label={metric.label}
                    caption={metric.caption}
                    value={numbers ? formatCount(numbers.current) : ''}
                    change={
                      numbers
                        ? percentChange(numbers.current, numbers.previous)
                        : { percent: null, direction: 'flat' }
                    }
                    loading={loading}
                    testId={`dashboard-${testIdPart(metric.id)}-card`}
                  />
                );
              })}
            </StatCardRow>

            <Box sx={twoUp('minmax(0, 2fr) minmax(0, 1fr)')}>
              <EngagementTrendCard series={data?.series} />
              <CurrentActivityCard activity={data?.activity} />
            </Box>
          </>
        )}

        <Box sx={twoUp('repeat(2, minmax(0, 1fr))')}>
          {METRIC_TABLES.map((table) => (
            <MetricTable
              key={table.id}
              table={table.id}
              caption={table.caption}
              rows={data?.rows}
              previous={data?.previousRows}
              loading={loading}
              error={failed ? 'These numbers could not be loaded.' : null}
              onRetry={retry}
            />
          ))}
        </Box>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="dashboard-footnote-message"
        sx={{ maxWidth: 760 }}
      >
        {FOOTNOTE}
      </Typography>
    </Box>
  );
}
